import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import {
  fetchModulePlans,
  fetchAutomationCategoriesByModule,
} from "./scheduledAutomationsApi";
import "./ModuleCategoryPicker";

/**
 * Step 1: pick a Module (GET /module-plans/module-plans), shown as a row of
 * small badges.
 * Step 2: pick a Category under that Module
 *         (GET /automation-categories/module/{moduleId}), shown as a nested
 *         row of badges directly under the selected module.
 * Calls onCategorySelect(category, module) once both are chosen.
 */
const ModuleCategoryPicker = ({ onCategorySelect }) => {
  const [modules, setModules] = useState([]);
  const [loadingModules, setLoadingModules] = useState(true);

  const [selectedModule, setSelectedModule] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  useEffect(() => {
    fetchModulePlans()
      .then((res) => setModules(res.data?.data || []))
      .catch(() => toast.error("Couldn't load modules."))
      .finally(() => setLoadingModules(false));
  }, []);

  const handleSelectModule = async (module) => {
    if (selectedModule?.moduleId === module.moduleId) return;
    setSelectedModule(module);
    setSelectedCategoryId(null);
    setCategories([]);
    setLoadingCategories(true);
    try {
      const res = await fetchAutomationCategoriesByModule(module.moduleId);
      const nextCategories = res.data?.data || [];
      setCategories(nextCategories);
      if (nextCategories.length) {
        setSelectedCategoryId(nextCategories[0].categoryId);
        onCategorySelect(nextCategories[0], module);
      }
    } catch (err) {
      toast.error("Couldn't load categories for this module.");
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleSelectCategory = (category) => {
    setSelectedCategoryId(category.categoryId);
    onCategorySelect(category, selectedModule);
  };

  if (loadingModules) {
    return (
      <div className="module-category-picker">
        <div className="automation-table__state">Loading modules…</div>
      </div>
    );
  }

  if (!modules.length) {
    return (
      <div className="module-category-picker">
        <p className="empty-hint">No modules found.</p>
      </div>
    );
  }

  return (
    <div className="module-category-picker">
      <div className="module-badge-row">
        {modules.map((mod) => {
          const isActive = selectedModule?.moduleId === mod.moduleId;
          return (
            <button
              key={mod.moduleId}
              type="button"
              className={`module-badge ${isActive ? "module-badge--active" : ""}`}
              onClick={() => handleSelectModule(mod)}
              title={mod.description || mod.moduleName}
            >
              {mod.moduleName}
            </button>
          );
        })}
      </div>

      {selectedModule && (
        <div className="category-badge-panel">
          <span className="category-badge-panel__label">
            Categories in {selectedModule.moduleName}
          </span>

          {loadingCategories ? (
            <div className="automation-table__state automation-table__state--inline">
              Loading categories…
            </div>
          ) : categories.length === 0 ? (
            <p className="empty-hint">No categories found for this module.</p>
          ) : (
            <div className="category-badge-row">
              {categories.map((cat) => {
                const isActive = selectedCategoryId === cat.categoryId;
                return (
                  <button
                    key={cat.categoryId}
                    type="button"
                    className={`category-badge ${isActive ? "category-badge--active" : ""}`}
                    onClick={() => handleSelectCategory(cat)}
                  >
                    {cat.categoryName}
                    {isActive && (
                      <Icon
                        icon="mdi:check-circle"
                        width="14"
                        className="category-badge__check"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ModuleCategoryPicker;
